(() => {
  class GigantiProduct extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      const variantData = this.querySelector('[data-product-variants]');
      try {
        this.variants = JSON.parse(variantData?.textContent || '[]');
      } catch (error) {
        console.error('Could not read product variants.', error);
        this.variants = [];
      }

      this.variantSelect = this.querySelector('[data-variant-select]');
      this.purchasePanel = this.querySelector('[data-purchase-panel]');
      this.requestPanel = this.querySelector('[data-request-panel]');
      this.currentPrice = this.querySelector('[data-current-price]');
      this.comparePrice = this.querySelector('[data-compare-price]');
      this.availability = this.querySelector('[data-availability]');

      this.variantSelect?.addEventListener('change', (event) => {
        const variant = this.variants.find((item) => item.id === event.target.value);
        if (variant) this.updateVariant(variant, { updateUrl: true, focusMedia: true });
      });

      const initialVariantId = this.variantSelect?.value
        || this.querySelector('[data-purchase-variant-id]')?.value
        || this.querySelector('[data-inquiry-variant-id]')?.value;
      const initialVariant = this.variants.find((item) => item.id === initialVariantId);
      if (initialVariant) this.updateVariant(initialVariant, { updateUrl: false, focusMedia: false });

      this.setupGallery();
      this.setupLightbox();
      if (this.variants.length) this.classList.add('product-js-ready');
    }

    updateVariant(variant, { updateUrl = true, focusMedia = false } = {}) {
      const canPurchase = Number(variant.price) > 0 && Number(variant.inventoryQuantity) > 0;

      if (this.currentPrice) {
        this.currentPrice.textContent = canPurchase ? variant.priceFormatted : 'Price on request';
      }

      if (this.comparePrice) {
        const showComparePrice = canPurchase && Number(variant.compareAtPrice) > Number(variant.price);
        this.comparePrice.hidden = !showComparePrice;
        this.comparePrice.textContent = showComparePrice ? variant.compareAtPriceFormatted : '';
      }

      if (this.availability) {
        this.availability.textContent = canPurchase
          ? 'Available for immediate purchase'
          : 'Contact us to confirm price and availability';
      }

      if (this.purchasePanel) this.purchasePanel.hidden = !canPurchase;
      if (this.requestPanel) this.requestPanel.hidden = canPurchase;

      this.querySelectorAll('[data-product-form] input, [data-product-form] button, [data-product-form] select')
        .forEach((control) => { control.disabled = !canPurchase; });

      const variantInput = this.querySelector('[data-purchase-variant-id]');
      if (variantInput) variantInput.value = variant.id;

      const quantityInput = this.querySelector('[data-quantity-input]');
      if (quantityInput) {
        quantityInput.max = Math.max(1, Number(variant.inventoryQuantity));
        if (Number(quantityInput.value) > Number(quantityInput.max)) quantityInput.value = quantityInput.max;
      }

      this.querySelectorAll('[data-inquiry-variant-id]').forEach((input) => { input.value = variant.id; });
      this.querySelectorAll('[data-inquiry-variant-title]').forEach((input) => { input.value = variant.title; });
      this.querySelectorAll('[data-inquiry-sku]').forEach((input) => { input.value = variant.sku || ''; });

      const productUrl = new URL(this.dataset.productUrl, window.location.origin);
      productUrl.searchParams.set('variant', variant.id);
      this.querySelectorAll('[data-inquiry-product-url]').forEach((input) => { input.value = productUrl.href; });

      if (updateUrl) {
        window.history.replaceState({}, '', `${productUrl.pathname}${productUrl.search}${productUrl.hash}`);
      }

      if (focusMedia && variant.featuredMediaId) this.showMedia(variant.featuredMediaId);
    }

    setupGallery() {
      this.querySelectorAll('[data-media-dot]').forEach((dot) => {
        dot.addEventListener('click', () => this.showMedia(dot.dataset.mediaId));
      });

      const gallery = this.querySelector('.product-gallery__grid');
      if (!gallery || !window.IntersectionObserver || !window.matchMedia('(max-width: 749px)').matches) return;

      const observer = new IntersectionObserver((entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) this.markCurrentMedia(visible.target.dataset.mediaId);
      }, { root: gallery, threshold: [0.55, 0.8] });

      this.querySelectorAll('[data-media-item]').forEach((item) => observer.observe(item));
    }

    showMedia(mediaId) {
      const media = this.querySelector(`[data-media-item][data-media-id="${CSS.escape(String(mediaId))}"]`);
      if (!media) return;
      this.markCurrentMedia(mediaId);
      media.scrollIntoView({ behavior: this.prefersReducedMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
    }

    markCurrentMedia(mediaId) {
      this.querySelectorAll('[data-media-item]').forEach((item) => {
        item.classList.toggle('product-gallery__item--selected', item.dataset.mediaId === String(mediaId));
      });
      this.querySelectorAll('[data-media-dot]').forEach((dot) => {
        dot.setAttribute('aria-current', String(dot.dataset.mediaId === String(mediaId)));
      });
    }

    setupLightbox() {
      this.lightbox = this.querySelector('[data-product-lightbox]');
      this.lightboxImage = this.querySelector('[data-lightbox-image]');
      if (!this.lightbox || !this.lightboxImage) return;

      this.lightboxTriggers = Array.from(this.querySelectorAll('[data-media-open]'));
      this.lightboxIndex = 0;
      this.lightboxRequestId = 0;
      this.lightboxPreloads = new Map();

      this.lightboxTriggers.forEach((trigger, index) => {
        trigger.addEventListener('click', () => {
          this.returnFocus = trigger;
          if (typeof this.lightbox.showModal === 'function') this.lightbox.showModal();
          this.showLightboxImage(index);
        });
      });

      this.querySelector('[data-lightbox-previous]')?.addEventListener('click', () => {
        this.showLightboxImage(this.lightboxIndex - 1);
      });
      this.querySelector('[data-lightbox-next]')?.addEventListener('click', () => {
        this.showLightboxImage(this.lightboxIndex + 1);
      });
      this.querySelector('[data-lightbox-close]')?.addEventListener('click', () => this.lightbox.close());
      this.lightbox.addEventListener('click', (event) => {
        if (event.target === this.lightbox) this.lightbox.close();
      });
      this.lightbox.addEventListener('keydown', (event) => {
        if (this.lightboxTriggers.length < 2) return;
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.showLightboxImage(this.lightboxIndex - 1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.showLightboxImage(this.lightboxIndex + 1);
        }
      });
      this.lightbox.addEventListener('close', () => {
        this.lightboxRequestId += 1;
        this.lightboxImage.removeAttribute('src');
        this.lightboxImage.removeAttribute('srcset');
        this.lightboxImage.removeAttribute('sizes');
        this.returnFocus?.focus();
      });
    }

    showLightboxImage(index) {
      const imageCount = this.lightboxTriggers?.length || 0;
      if (!imageCount) return;

      const requestId = ++this.lightboxRequestId;
      this.lightboxIndex = (index + imageCount) % imageCount;
      const trigger = this.lightboxTriggers[this.lightboxIndex];
      const galleryImage = trigger.querySelector('img');
      const previewSrc = galleryImage?.currentSrc || (
        galleryImage?.complete && galleryImage.naturalWidth
          ? galleryImage.src
          : trigger.dataset.previewSrc
      );

      this.lightboxImage.removeAttribute('srcset');
      this.lightboxImage.removeAttribute('sizes');
      this.lightboxImage.width = Number(trigger.dataset.fullWidth) || 1800;
      this.lightboxImage.height = Number(trigger.dataset.fullHeight) || 1800;
      this.lightboxImage.src = previewSrc || trigger.dataset.fullSrc;
      this.lightboxImage.alt = trigger.dataset.mediaAlt || '';

      this.loadLightboxImage(trigger, 'high')
        .then((image) => {
          if (requestId !== this.lightboxRequestId || !this.lightbox.open) return;
          this.lightboxImage.src = image.currentSrc || trigger.dataset.fullSrc;
          this.preloadNextLightboxImage(this.lightboxIndex + 1);
        })
        .catch(() => {});
    }

    loadLightboxImage(trigger, fetchPriority = 'auto') {
      const sizes = `${Math.ceil(this.getLightboxDisplayWidth(trigger))}px`;
      const cacheKey = `${trigger.dataset.fullSrc}|${sizes}`;
      const cached = this.lightboxPreloads.get(cacheKey);

      if (cached) {
        if (fetchPriority === 'high') cached.image.fetchPriority = 'high';
        return cached.promise;
      }

      const image = new Image();
      image.decoding = 'async';
      image.fetchPriority = fetchPriority;

      const promise = new Promise((resolve, reject) => {
        image.addEventListener('load', async () => {
          try {
            await image.decode();
          } catch (error) {
            // A completed load is still usable when a browser declines decode().
          }
          resolve(image);
        }, { once: true });
        image.addEventListener('error', reject, { once: true });
      });

      this.lightboxPreloads.set(cacheKey, { image, promise });
      promise.catch(() => this.lightboxPreloads.delete(cacheKey));
      image.sizes = sizes;
      image.srcset = trigger.dataset.fullSrcset || '';
      image.src = trigger.dataset.fullSrc;

      return promise;
    }

    getLightboxDisplayWidth(trigger) {
      const imageBox = this.lightboxImage.getBoundingClientRect();
      const availableWidth = Math.max(1, imageBox.width);
      const availableHeight = Math.max(1, imageBox.height);
      const sourceWidth = Number(trigger.dataset.fullWidth) || availableWidth;
      const sourceHeight = Number(trigger.dataset.fullHeight) || availableHeight;

      return Math.min(availableWidth, availableHeight * (sourceWidth / sourceHeight));
    }

    preloadNextLightboxImage(index) {
      if (this.lightboxTriggers.length < 2) return;

      const requestId = this.lightboxRequestId;
      const preload = () => {
        if (!this.lightbox.open || requestId !== this.lightboxRequestId) return;
        const nextIndex = (index + this.lightboxTriggers.length) % this.lightboxTriggers.length;
        this.loadLightboxImage(this.lightboxTriggers[nextIndex], 'low').catch(() => {});
      };

      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(preload, { timeout: 1200 });
      } else {
        window.setTimeout(preload, 200);
      }
    }

    get prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  if (!customElements.get('giganti-product')) customElements.define('giganti-product', GigantiProduct);
})();
